import { ApiError } from '@/lib/api-client'
import type { AuthUser } from '../types'

/**
 * Auth API service — comunicación con las rutas de auth propias (/api/auth/*).
 *
 * Flujo:
 *  1. Las rutas de Next.js emiten un JWT propio (jose) en cookies httpOnly
 *     (`sk_access_token` / `sk_refresh_token`).
 *  2. Este servicio hace fetch same-origin: la cookie viaja automáticamente,
 *     sin headers Authorization.
 */

export interface BackendUser {
	id: string
	email: string
	role: AuthUser['role']
	firstName?: string
	lastName?: string
	avatarUrl?: string | null
	profileName?: string
	isOnboarded: boolean
	createdAt: string
	updatedAt?: string
}

/** Respuesta de GET /api/auth/me — incluye la empresa primaria del usuario. */
export interface MeResponse extends BackendUser {
	companyId: string | null
}

export interface CompleteProfilePayload {
	supabaseId: string
	// Los campos extendidos de empresa se guardan vía /api/sinko/company/setup.
	name?: string
}

/** Fetch same-origin a /api/auth/* — lanza ApiError con el mensaje del servidor. */
async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await fetch(path, {
		headers: { 'Content-Type': 'application/json' },
		...init,
	})

	if (!res.ok) {
		const data = (await res.json().catch(() => ({}))) as { message?: string }
		throw new ApiError(data.message ?? `HTTP ${res.status}: ${res.statusText}`, res.status, data)
	}

	return (await res.json()) as T
}

export const authService = {
	/**
	 * Obtiene el perfil del usuario autenticado (JWT propio en cookie).
	 * Incluye `companyId` (empresa primaria en sinko_db o null).
	 */
	async getMe(): Promise<MeResponse> {
		return authFetch<MeResponse>('/api/auth/me')
	},

	/**
	 * Sincroniza al usuario tras el signup (idempotente).
	 * La sesión viaja en la cookie; no requiere headers extra.
	 */
	async registerInBackend(): Promise<BackendUser> {
		return authFetch<BackendUser>('/api/auth/register', {
			method: 'POST',
			body: JSON.stringify({}),
		})
	},

	/**
	 * Notifica el login (métricas). No-op: last_login se maneja en el login propio.
	 */
	async notifyLogin(): Promise<void> {
		return Promise.resolve()
	},

	/**
	 * Verifica que la sesión (cookie) sea válida.
	 * Útil para validar sesión al cargar la app.
	 */
	async verifySession(): Promise<{ valid: boolean; user: BackendUser | null }> {
		try {
			const user = await this.getMe()
			return { valid: true, user }
		} catch {
			return { valid: false, user: null }
		}
	},

	/**
	 * Marca el perfil como completado en el backend propio.
	 * Los datos de empresa se persisten aparte vía /api/sinko/company/setup.
	 */
	async completeProfile(payload: CompleteProfilePayload): Promise<BackendUser> {
		const user = await authFetch<BackendUser>('/api/auth/complete-profile', {
			method: 'POST',
			body: JSON.stringify({}),
		})
		return { ...user, profileName: payload.name ?? user.profileName }
	},

	/**
	 * Cambia la contraseña del usuario autenticado.
	 */
	async changePassword(newPassword: string): Promise<void> {
		await authFetch<{ success: boolean }>('/api/auth/change-password', {
			method: 'POST',
			body: JSON.stringify({ newPassword }),
		})
	},

	/**
	 * Solicita recuperación de contraseña.
	 * Lanza ApiError con el mensaje del servidor si no está disponible (501).
	 */
	async forgotPassword(email: string): Promise<void> {
		await authFetch<{ message?: string }>('/api/auth/forgot-password', {
			method: 'POST',
			body: JSON.stringify({ email }),
		})
	},
}
